import Cookies from "js-cookie";
import { parseLeadCaptureBotType } from "../pages/LandingPages/main/modal/leadCaptureEmployeeOptions.js";

export const LEAD_ID_COOKIE = "fl_lead_id";
export const LEAD_ID_PARAM = "l";
export const LEAD_EMAIL_COOKIE = "fl_lead_email";
export const LEAD_EMAIL_PARAM = "email";
export const LEAD_EMAIL_LEGACY_PARAM = "lead_email";
export const LEAD_BRAIN_COOKIE = "fl_lead_brain";
export const LEAD_BRAIN_PARAM = "brain";
export const LEAD_BRAIN_LEGACY_PARAMS = ["bot_type", "lead_bot_type"];

const LEAD_ID_MAX_AGE_DAYS = 30;
const LEAD_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function getLeadCookieOptions(hostname) {
  const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";
  const host = String(
    hostname !== undefined ? hostname : typeof window !== "undefined" ? window.location.hostname : "",
  ).toLowerCase();
  const options = {
    expires: LEAD_ID_MAX_AGE_DAYS,
    secure: isSecure,
    sameSite: "lax",
    path: "/",
  };
  if (host === "fractolabs.com" || host.endsWith(".fractolabs.com")) {
    options.domain = ".fractolabs.com";
  } else if (host === "settrhq.com" || host.endsWith(".settrhq.com")) {
    options.domain = ".settrhq.com";
  }
  return options;
}

function normalizeSearch(search = "") {
  return search.startsWith("?") ? search.slice(1) : search;
}

function getSearchParams(search = "") {
  return new URLSearchParams(normalizeSearch(search));
}

function normalizeLeadEmail(raw) {
  const value = String(raw || "").trim().toLowerCase();
  return LEAD_EMAIL_RE.test(value) ? value : null;
}

export function getLeadIdFromSearch(search = "") {
  const value = getSearchParams(search).get(LEAD_ID_PARAM)?.trim();
  return value || null;
}

export function getLeadEmailFromSearch(search = "") {
  const params = getSearchParams(search);
  return (
    normalizeLeadEmail(params.get(LEAD_EMAIL_PARAM))
    || normalizeLeadEmail(params.get(LEAD_EMAIL_LEGACY_PARAM))
  );
}

export function getLeadBrainFromSearch(search = "") {
  const params = getSearchParams(search);
  const candidates = [
    params.get(LEAD_BRAIN_PARAM),
    ...LEAD_BRAIN_LEGACY_PARAMS.map((key) => params.get(key)),
  ];
  for (const raw of candidates) {
    const parsed = parseLeadCaptureBotType(raw);
    if (parsed) {
      return parsed;
    }
  }
  return null;
}

export function getStoredLeadId() {
  return Cookies.get(LEAD_ID_COOKIE)?.trim() || null;
}

export function getStoredLeadEmail() {
  return normalizeLeadEmail(Cookies.get(LEAD_EMAIL_COOKIE));
}

export function getStoredLeadBrain() {
  return parseLeadCaptureBotType(Cookies.get(LEAD_BRAIN_COOKIE));
}

/** URL param wins; if present — persist to cookie and return lead id. */
export function syncLeadIdFromSearch(search = "") {
  const fromUrl = getLeadIdFromSearch(search);
  if (fromUrl) {
    Cookies.set(LEAD_ID_COOKIE, fromUrl, getLeadCookieOptions());
    return fromUrl;
  }
  return getStoredLeadId();
}

/** URL param wins; if present and valid — persist to cookie and return email. */
export function syncLeadEmailFromSearch(search = "") {
  const fromUrl = getLeadEmailFromSearch(search);
  if (fromUrl) {
    Cookies.set(LEAD_EMAIL_COOKIE, fromUrl, getLeadCookieOptions());
    return fromUrl;
  }
  return getStoredLeadEmail();
}

/** URL param wins; if present and valid — persist to cookie and return brain. */
export function syncLeadBrainFromSearch(search = "") {
  const fromUrl = getLeadBrainFromSearch(search);
  if (fromUrl) {
    Cookies.set(LEAD_BRAIN_COOKIE, fromUrl, getLeadCookieOptions());
    return fromUrl;
  }
  return getStoredLeadBrain();
}

/** Sync l, email, and brain from URL into cookies. */
export function syncLeadAttributionFromSearch(search = "") {
  return {
    leadId: syncLeadIdFromSearch(search),
    email: syncLeadEmailFromSearch(search),
    brain: syncLeadBrainFromSearch(search),
  };
}
